// Timber lane edging (low boards on stakes between the lanes) and the banner hoardings that
// close the course on both sides.
import {
  Group, InstancedMesh, Matrix4, Mesh, MeshStandardMaterial, PlaneGeometry, Quaternion, Vector3,
} from 'three';
import { TRACK_LENGTH } from '../../data/track';
import { HOARDING, edgingZ, rng } from './layout';
import type { EnvMaterials } from './materials';
import { HOARDING_DESIGNS, hoardingAtlas } from './signs';
import { box, merge } from './util';

const EDGE = { from: -0.3, to: TRACK_LENGTH + 46, height: 0.17, thick: 0.06, postEvery: 2.4 };

export class Rails {
  readonly group = new Group();

  constructor(mats: EnvMaterials) {
    this.group.name = 'rails';
    const len = EDGE.to - EDGE.from;
    const zs = edgingZ();

    // Edging boards: one long board per line (two stacked planks), with a slightly varied shade.
    const boards = zs.map((z, i) => merge([
      box(len, EDGE.height * 0.5, EDGE.thick, { at: [EDGE.from + len / 2, EDGE.height * 0.25, z], tile: 1.8, shade: 0.82 + (i % 3) * 0.05 }),
      box(len, EDGE.height * 0.5, EDGE.thick, { at: [EDGE.from + len / 2, EDGE.height * 0.75 + 0.005, z], tile: 1.8, shade: 0.9 + (i % 2) * 0.06 }),
    ]));
    const edging = new Mesh(merge(boards), mats.planks);
    edging.castShadow = true; edging.receiveShadow = true;
    edging.name = 'lane-edging';
    this.group.add(edging);

    // Stakes along every board.
    const per = Math.floor(len / EDGE.postEvery) + 1;
    const stakeGeo = box(0.09, EDGE.height + 0.08, 0.09, { at: [0, (EDGE.height + 0.08) / 2, 0], tile: 0.6, grain: 'y' });
    const stakes = new InstancedMesh(stakeGeo, mats.timber, per * zs.length);
    const r = rng(21);
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(1, 1, 1), p = new Vector3();
    let n = 0;
    for (const z of zs) for (let k = 0; k < per; k++) {
      p.set(EDGE.from + k * EDGE.postEvery, 0, z + (k % 2 ? 0.075 : -0.075));
      q.setFromAxisAngle(new Vector3(0, 1, 0), (r() - 0.5) * 0.08);
      s.set(1, 0.9 + r() * 0.2, 1);
      stakes.setMatrixAt(n++, m.compose(p, q, s));
    }
    stakes.castShadow = true; stakes.receiveShadow = true;
    this.group.add(stakes);

    // Hoardings: banner boards on timber frames, both sides, faces towards the track.
    const atlas = hoardingAtlas();
    const face = new MeshStandardMaterial({ map: atlas, roughness: 0.82 });
    const count = Math.ceil((HOARDING.to - HOARDING.from) / HOARDING.panel);
    const faceGeos = Array.from({ length: HOARDING_DESIGNS }, (_, d) => {
      const g = new PlaneGeometry(HOARDING.panel - 0.08, HOARDING.height - 0.12);
      const uv = g.getAttribute('uv');
      for (let k = 0; k < uv.count; k++) uv.setY(k, 1 - (d + 1 - uv.getY(k)) / HOARDING_DESIGNS);
      return g;
    });
    const perDesign = faceGeos.map((g) => new InstancedMesh(g, face, count * 2));
    const used = new Array(HOARDING_DESIGNS).fill(0) as number[];
    const frameGeo = merge([
      box(HOARDING.panel, HOARDING.height, 0.05, { at: [0, HOARDING.height / 2 + 0.08, -0.03], tile: 1.8, shade: 0.75 }),
      box(HOARDING.panel + 0.02, 0.08, 0.12, { at: [0, HOARDING.height + 0.12, -0.02], tile: 1.8 }),
      box(0.1, HOARDING.height + 0.16, 0.1, { at: [-HOARDING.panel / 2, (HOARDING.height + 0.16) / 2, -0.08], tile: 0.8, grain: 'y' }),
    ]);
    const frames = new InstancedMesh(frameGeo, mats.timber, count * 2);
    let f = 0;
    for (const side of [-1, 1]) {
      // Front faces: −z side faces +z (towards the track) and vice versa.
      const rotY = side < 0 ? 0 : Math.PI;
      q.setFromAxisAngle(new Vector3(0, 1, 0), rotY);
      for (let i = 0; i < count; i++) {
        const x = HOARDING.from + (i + 0.5) * HOARDING.panel;
        p.set(x, 0, side * HOARDING.z);
        frames.setMatrixAt(f++, m.compose(p, q, s.set(1, 1, 1)));
        const d = (i * 7 + (side > 0 ? 3 : 0)) % HOARDING_DESIGNS;
        p.set(x, HOARDING.height / 2 + 0.08, side * HOARDING.z + (side < 0 ? 0.003 : -0.003));
        perDesign[d]!.setMatrixAt(used[d]!++, m.compose(p, q, s));
      }
    }
    perDesign.forEach((im, d) => { im.count = used[d]!; im.receiveShadow = true; im.castShadow = false; im.name = `hoarding-${d}`; this.group.add(im); });
    frames.castShadow = true; frames.receiveShadow = true;
    this.group.add(frames);
  }
}
